import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import Navbar from './Navbar';
import Login from './Login';
import Dashboard from './Dashboard';
import ChangePassword from './ChangePassword';
import Employees from './employees';
import Customers from './Customers';
import FilmSizes from './FilmSizes';
import FilmRates from './FilmRates';
import ObservationAndRemarks from './ObservationAndRemarks';
import Reports from './Reports';
import NewReport from './NewReport';
import CustomReports from './CustomReports';
import NewCustomReport from './NewCustomReport';
import Invoices from './Invoices';
import NewInvoice from './NewInvoice';


const TokenExpiryWatcher = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = Cookies.get('token');

      // If no token and not already on login page — redirect
      if (!token) {
        if (window.location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now();
        const expirationTime = decoded.exp * 1000;

        if (currentTime >= expirationTime) {
          // Token expired — clear cookies and redirect
          Cookies.remove('token');
          Cookies.remove('userName');
          Cookies.remove('userRole');
          navigate('/login', { replace: true });
        }
      } catch (error) {
        // Invalid/corrupted token — clear and redirect
        Cookies.remove('token');
        Cookies.remove('userName');
        Cookies.remove('userRole');
        navigate('/login', { replace: true });
      }
    };

    // Run immediately when any page loads
    checkTokenExpiry();

    // Then keep checking every 30 seconds
    const interval = setInterval(checkTokenExpiry, 30000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [navigate]);

  // Renders nothing — only watches token
  return null;
};


const ProtectedRoute = ({ children }) => {
  const token = Cookies.get('token');
  return token ? children : <Navigate to="/login" replace />;
};


const PublicRoute = ({ children }) => {
  const token = Cookies.get('token');
  return token ? <Navigate to="/dashboard" replace /> : children;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const hideNavbarRoutes = ['/login', '/signup'];
  const shouldShowNavbar = !hideNavbarRoutes.includes(location.pathname);

  return (
    <div className="App">
      {shouldShowNavbar && <Navbar />}
      <div className={shouldShowNavbar ? "pt-16" : ""}>
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      {/*TokenExpiryWatcher is INSIDE <Router> so useNavigate works
             It is OUTSIDE <Routes> so it always runs on ALL pages */}
      <TokenExpiryWatcher />

      <Layout>
        <Routes>
          {/* Default Route */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Public Routes */}
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Customers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/films/film-size"
            element={
              <ProtectedRoute>
                <FilmSizes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/films/film-rates"
            element={
              <ProtectedRoute>
                <FilmRates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/films/observation-and-remarks"
            element={
              <ProtectedRoute>
                <ObservationAndRemarks />
              </ProtectedRoute>
            }
          />

          {/* Reports Routes */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/new"
            element={
              <ProtectedRoute>
                <NewReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/edit/:id"
            element={
              <ProtectedRoute>
                <NewReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/view/:id"
            element={
              <ProtectedRoute>
                <NewReport />
              </ProtectedRoute>
            }
          />

          {/* Custom Reports Routes */}
          <Route
            path="/custom-reports"
            element={
              <ProtectedRoute>
                <CustomReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-reports/new-custom-report"
            element={
              <ProtectedRoute>
                <NewCustomReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-reports/edit/:id"
            element={
              <ProtectedRoute>
                <NewCustomReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-reports/view/:id"
            element={
              <ProtectedRoute>
                <NewCustomReport />
              </ProtectedRoute>
            }
          />

          {/* Invoice Routes */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute>
                <NewInvoice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/edit/:id"
            element={
              <ProtectedRoute>
                <NewInvoice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/view/:id"
            element={
              <ProtectedRoute>
                <NewInvoice />
              </ProtectedRoute>
            }
          />

          {/* 404 Route */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;